import { isEqual, has, get, set, toPath, sortBy, transform, forEach, keys, uniq, cloneDeep, isEmpty } from "lodash";

import { FIELD_TYPES } from "globals/constants";
import { RESEARCH_TEAM } from "globals/constants/formObjects";

const { ASSOCIATION_TYPE } = RESEARCH_TEAM;

/**
 * @typedef {object} ComparedObject
 * @property {string} path
 * @property {Record<string, any>} field
 * @property {boolean} conflict
 * @property {any} value
 * @property {any|undefined} oldValue
 * @property {boolean} replaceValue
 */

/**
 * @typedef {Record<string, ComparedObject>} ComparedData
 */

/**
 * @typedef {[string, any][]} PathValueArray
 */

/**
 * @typedef {object} Options
 * @property {object[]} formElements
 * @property {string} payloadKey
 * @property {Set<string>} changedFormKeys
 */

export class CompareSnapshotData {
	constructor () {
		/** @type {Map<string, object>} */
		this.formElements = new Map();
		/** @link {Options["payloadKey"]} */
		this.payloadKey = "";
		/** @see {Options["changedFormKeys"]} */
		this.changedFormKeys = new Set();

		/** @type {Record<string, any>} */
		this.sourceObj = {};
		/** @type {Record<string, any>} */
		this.changesObj = {};
		/** @type {Record<string, any>} */
		this.lastSavedObj = {};
	}

	/**
	 * * Check object is some type of object by given keys
	 * @private
	 * @param {object | object[]} object
	 * @param {string[]} key
	 * @returns {boolean}
	 */
	#isObjectByKey = (object, key) => {
		return key.some(k => has(Array.isArray(object) ? object[0] : object, k));
	}

	/**
	 * * Check object is flowable file object by "downloadUrl" keys
	 * @private
	 * @param {object} object
	 * @returns {boolean}
	 */
	#isFileObject = object => this.#isObjectByKey(object, [ "downloadUrl", "uploadMetadataId" ])

	/**
	 * * Format field value. If value is an array, it will be sorted by some property.
	 * @private
	 * @param {object} acc
	 * @param {any} value
	 * @param {string} key
	 * @returns {Record<string, any>}
	 */
	#formatFieldValue = (acc, value, key) => {
		let v = value;
		if (this.#isFileObject(value)) {
			v = sortBy(value, [ "id" ]);
		}
		acc[key] = v;

		return acc;
	};

	/**
	 * * Returns field object and field path.
	 * @private
	 * @param {string} key
	 * @returns {{path: string, field: object}}
	 */
	#getFieldAndPath = key => {
		return {
			field: this.formElements.get(key),
			path: [ ...toPath(this.payloadKey), key ].join("."),
		};
	};

	/**
	 * * Compares 2 and more objects or primitive types.
	 * @param {any[]} args
	 * @returns {boolean}
	 */
	#areEqual = (...args) => {
		for (let i = 0; i < args.length; i++) {
			for (let j = i + 1; j < args.length; j++) {
				if (!isEqual(args[i], args[j])) {
					return false;
				}
			}
		}

		return true;
	}

	/**
	 * Format value by field type.
	 * if field type is "upload", value (array) compared by object name and object size.
	 * @private
	 * @param {object} field
	 * @param {any} value
	 * @returns {any}
	 */
	formatValueByFieldType = (field, value) => {
		let formattedValue = value;

		if ([ FIELD_TYPES.UPLOAD, "upload" ].includes(field?.cayuseType ?? field?.type)) {
			// the value is already sorted by ID, to exclude wrong comparisons if some array indexes have changed
			formattedValue = Array.isArray(value) ? value.map(file => `${file.name}_${file.size}`) : value;
		}

		if (field?.type === FIELD_TYPES.RESEARCH_TEAM) {
			if (formattedValue) {
				formattedValue = formattedValue.map(rto => {
					const { member = null, role = null, guest = null,  associationType, associations, externalAssociations } = rto;
					if (rto.associationType === ASSOCIATION_TYPE.EXTERNAL) {
						return {
							member,
							role,
							associationType,
							externalAssociations,
							guest,
						};
					} else {
						return {
							member,
							role,
							associationType,
							associations,
						};
					}
				});
			}
			formattedValue = (formattedValue ?? []).map(({ isInitialCalculationRequired, ...rest }) => rest);
		} else if (field?.type === FIELD_TYPES.CLASSIFICATION_CODES) {
			if (formattedValue) {
				formattedValue = formattedValue.map(classificationCode => {
					const { code, percentage } = classificationCode;

					return {
						code,
						percentage: percentage === null ? '' : percentage
					};
				});
			}
		}

		return formattedValue;
	}

	/**
	 * * Compare values and transform value before comparing by field type.
	 * @param {object} field
	 * @param {any} sourceValue
	 * @param {any} changedValue
	 * @param {any} serverValue
	 * @returns {{ notChanged: boolean, isConflict: boolean, changedOnServer: boolean, changedByUser: boolean }}
	 */
	#compareValue = (field, sourceValue, changedValue, serverValue) => {
		const sourceV = this.formatValueByFieldType(field, sourceValue);
		const changedV = this.formatValueByFieldType(field, changedValue);
		const serverV = this.formatValueByFieldType(field, serverValue);

		const notChanged = this.#areEqual(sourceV, changedV, serverV);

		const changedByUser = !isEqual(sourceV, changedV);
		const changedOnServer = !isEqual(sourceV, serverV);
		const isConflict = changedByUser && changedOnServer && !isEqual(changedV, serverV);

		/* istanbul ignore next */
		if (sourceV === undefined || changedV === undefined || serverV === undefined) {
			console.groupCollapsed(field?.value);
			console.log("sourceV: ", sourceV);
			console.log("changedV: ", changedV);
			console.log("serverV: ", serverV);
			console.log("changedByUser: ", changedByUser);
			console.log("changedOnServer: ", changedOnServer);
			console.log("notChanged: ", notChanged);
			console.log("isConflict: ", isConflict);
			console.groupEnd();
		}

		return {
			notChanged,
			isConflict,
			changedOnServer,
			changedByUser,
		};
	}

	/**
	 * * Compare each value by key in sourceObj, changesObj and lastSavedObj.
	 * @private
	 * @param {string} key
	 * @returns {ComparedObject|null}
	 */
	#compareBySingleKey = key => {
		let sourceValue = this.sourceObj[key];
		let changedValue = this.changesObj[key];
		let serverValue = this.lastSavedObj[key];

		/** @type {ComparedObject} */
		let comparedObject = {
			...this.#getFieldAndPath(key),
			conflict: false,
			replaceValue: true,
		};

		const { notChanged, isConflict, changedOnServer } = this.#compareValue(
			comparedObject.field,
			sourceValue,
			changedValue,
			serverValue,
		);

		if (notChanged) {
			return null;
		}

		if (isConflict) {
			comparedObject.conflict = true;
			comparedObject.value = serverValue;
			comparedObject.oldValue = changedValue;
		}
		else if (changedOnServer) {
			comparedObject.value = serverValue;
			comparedObject.oldValue = sourceValue;
		}
		else {
			comparedObject.value = changedValue;
			comparedObject.oldValue = sourceValue;
			comparedObject.replaceValue = false;
		}

		// if conflict and user did not touch this field
		if (comparedObject.conflict && !this.changedFormKeys.has(key)) {
			comparedObject.conflict = false;
			comparedObject.value = serverValue;
		}

		return comparedObject;
	}

	/**
	 * * Compare values by keys and find conflicts in sourceObj, changesObj and lastSavedObj.
	 * @private
	 * @param {string[]} keys
	 * @returns {{conflicts: ComparedObject[], comparedData: ComparedData}}
	 */
	#compareByKeys = keys => {
		const /** @type {ComparedObject[]} */ conflicts = [];
		const /** @type ComparedData */ comparedData = {};

		for (const key of keys) {
			const data = this.#compareBySingleKey(key);
			if (!data) {
				continue;
			}

			if (data.conflict) {
				conflicts.push(data);
			}
			if (data.replaceValue) {
				comparedData[key] = data;
			}
		}

		return { conflicts, comparedData };
	}

	/**
	 * * Transform data objects to key:value plain objects and collect all keys into array
	 * @private
	 * @param {Record<string, any>} sourceSnapshot
	 * @param {Record<string, any>} changesSnapshot
	 * @param {Record<string, any>} serverSnapshot
	 * @returns {string[]}
	 */
	#transformDataAndCollectAllKeys = (sourceSnapshot, changesSnapshot, serverSnapshot) => {
		this.sourceObj = transform(
			get(sourceSnapshot, this.payloadKey, {}),
			this.#formatFieldValue,
		);

		this.changesObj = transform(
			get(changesSnapshot, this.payloadKey, {}),
			this.#formatFieldValue,
		);

		this.lastSavedObj = transform(
			get(serverSnapshot, this.payloadKey, {}),
			this.#formatFieldValue,
		);

		return uniq([
			...keys(this.sourceObj),
			...keys(this.changesObj),
			...keys(this.lastSavedObj),
		]);
	}

	/**
	 * * Set compared data to first arg object.
	 * * Returns mutated first arg object and [path, value] for each changed object value.
	 * @private
	 * @param {Record<string, any>} mergedData
	 * @param {ComparedData} comparedData
	 * @returns {{mergedData: Record<string, any>, pathValueArray: PathValueArray}}
	 */
	#mergeComparedData = (mergedData, comparedData) => {
		/** @type PathValueArray */
		const pathValueArray = [];

		// update values and write to pathValueArray
		forEach(Object.values(comparedData), item => {
			pathValueArray.push([ item.path, item.value ]);
			set(mergedData, item.path, item.value);
		});

		return {
			pathValueArray,
			mergedData,
		};
	}

	/**
	 * Validates the options object for required properties.
	 * @private
	 * @param {Options} options
	 * @throws {Error} If any required property is missing or invalid.
	 */
	#validateOptions = ({ formElements, payloadKey, changedFormKeys }) => {
		if (isEmpty(formElements)) {
			throw new Error("Missing 'formElements' in '#init' function.");
		}
		if (isEmpty(payloadKey)) {
			throw new Error("Missing 'payloadKey' in '#init' function.");
		}
		if (!changedFormKeys) {
			throw new Error("Missing 'changedFormKeys' in '#init' function.");
		}
	};

	/**
	 * * Set additional data for comparing.
	 * @private
	 * @param {Options} options
	 */
	#init = options => {
		this.#validateOptions(options);

		const { formElements, payloadKey, changedFormKeys } = options;

		this.formElements = new Map(
			// 1. remove brackets from the string. e.g. {{some-field-key}} (useful for tests only.)
			// 2. the formElement value can contain the full path: root.awardContent.awardTitle
			formElements.map(item => {
				const value = item.value.replace(/{{|}}/g, "").split(".").splice(-1, 1)[0];
				item.value = value;

				return [ value, item ];
			})
		);

		this.payloadKey = payloadKey;
		this.changedFormKeys = new Set(Array.from(changedFormKeys));

		this.sourceObj = {};
		this.changesObj = {};
		this.lastSavedObj = {};
	}

	/**
	 * * Compare each value in sourceObj, changesObj and lastSavedObj.
	 * * Returns updated mergedData, conflicts and [path, value] for each changed object value.
	 * * NOTE: sourceSnapshot / changesSnapshot / serverSnapshot are deep copied before any actions
	 * @param {Record<string, any>} sourceSnapshot
	 * @param {Record<string, any>} changesSnapshot
	 * @param {Record<string, any>} serverSnapshot
	 * @param {Options} options
	 * @returns {{mergedData: Record<string, any>, pathValueArray: PathValueArray, conflicts: ComparedObject[]}}
	 */
	compare = (
		sourceSnapshot,
		changesSnapshot,
		serverSnapshot,
		options,
	) => {
		this.#init(options);

		const keys = this.#transformDataAndCollectAllKeys(
			cloneDeep(sourceSnapshot),
			cloneDeep(changesSnapshot),
			cloneDeep(serverSnapshot),
		);

		const { conflicts, comparedData } = this.#compareByKeys(keys);

		const { pathValueArray, mergedData } = this.#mergeComparedData(
			cloneDeep(changesSnapshot),
			comparedData,
		);

		/* istanbul ignore next */
		console.groupCollapsed("Comparing data");

		/* istanbul ignore next */
		console.dir(pathValueArray);
		/* istanbul ignore next */
		console.dir(conflicts);
		/* istanbul ignore next */
		console.dir(mergedData);

		/* istanbul ignore next */
		console.dir(JSON.parse(JSON.stringify(Array.from(this.changedFormKeys))));
		/* istanbul ignore next */
		console.dir(this);

		/* istanbul ignore next */
		console.groupEnd();

		return { pathValueArray, conflicts, mergedData };
	}
}
